import { useEffect, useRef, useState } from "react";
import { Bookmark, ChevronDown, Plus, Trash2, Check, Users } from "lucide-react";
import { Badge, Button, Input, Modal, ModalFooter, useToast, useConfirm } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { FilterSpec } from "./SmartFilterBuilder";

/**
 * A "saved view" bundles everything ContactsPage needs to reconstruct a
 * working view of the list: the selected smart list (or ad-hoc filter),
 * the sort order, and the free-text search. Stored verbatim in the view's
 * `config` JSON so applying a view round-trips the page state exactly.
 *
 * This is deliberately separate from smart lists: a smart list is a shared,
 * server-computed subset of contacts (with a live count); a saved view is a
 * personal shortcut to "how I like to look at the list right now" — filter +
 * sort + search together.
 */
export interface ContactsViewConfig {
  listId: string | null;
  filter: FilterSpec;
  sort: string;
  q?: string;
}

interface SavedView {
  id: string;
  surface: string;
  name: string;
  config: ContactsViewConfig;
  is_shared: boolean;
  created_at: string;
}

const SURFACE = "contacts";

export function SavedViews({
  getCurrentConfig,
  onApply,
}: {
  /** Snapshot of the page's current filter/sort/search — captured on save. */
  getCurrentConfig: () => ContactsViewConfig;
  /** Load a saved config back into the page. */
  onApply: (config: ContactsViewConfig) => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState<SavedView[]>([]);
  const [appliedId, setAppliedId] = useState<string | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const reload = async () => {
    try {
      const d = await api.get<{ views: SavedView[] }>("/saved-views", { surface: SURFACE });
      setViews(d.views ?? []);
    } catch { /* ignore — views are a convenience, never block the page */ }
  };
  useEffect(() => { void reload(); }, []);

  // Click-outside to dismiss the dropdown.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const apply = (v: SavedView) => {
    setAppliedId(v.id);
    setOpen(false);
    onApply(v.config);
    toast.success(`Applied "${v.name}"`);
  };

  const remove = async (v: SavedView) => {
    const ok = await confirm({
      title: `Delete "${v.name}"?`,
      description: "This removes the saved view. Your contacts and smart lists are not affected.",
      confirmLabel: "Delete view",
      danger: true,
    });
    if (!ok) return;
    setBusyId(v.id);
    try {
      await api.delete(`/saved-views/${v.id}`);
      setViews((prev) => prev.filter((x) => x.id !== v.id));
      if (appliedId === v.id) setAppliedId(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete view");
    } finally {
      setBusyId(null);
    }
  };

  const activeLabel = views.find((v) => v.id === appliedId)?.name ?? "Views";

  return (
    <div className="relative inline-block" ref={ref}>
      <div
        className={cn(
          "inline-flex h-9 items-center gap-1 rounded-lg border bg-zinc-900/50 text-sm transition-colors hover:bg-zinc-900",
          appliedId ? "border-indigo-500/40 text-zinc-100" : "border-zinc-800 text-zinc-300",
        )}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-full items-center gap-2 px-3"
        >
          <Bookmark className={cn("h-3.5 w-3.5 shrink-0", appliedId && "text-indigo-400")} />
          <span className="max-w-[9rem] truncate">{activeLabel}</span>
          <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform", open && "rotate-180")} />
        </button>
      </div>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl shadow-black/40 animate-fade-in">
          <div className="max-h-64 overflow-y-auto p-1">
            {views.length === 0 && (
              <p className="px-2 py-3 text-xs text-zinc-500">No saved views yet. Save your current filter + sort below.</p>
            )}
            {views.map((v) => (
              <div
                key={v.id}
                className={cn(
                  "group flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-sm hover:bg-zinc-900",
                  appliedId === v.id && "bg-zinc-900",
                )}
              >
                <button
                  type="button"
                  onClick={() => apply(v)}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                  {appliedId === v.id ? (
                    <Check className="h-3.5 w-3.5 shrink-0 text-indigo-400" />
                  ) : (
                    <Users className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
                  )}
                  <span className="truncate text-zinc-100">{v.name}</span>
                  {v.is_shared && <Badge color="zinc" size="xs">Shared</Badge>}
                </button>
                <button
                  type="button"
                  onClick={() => remove(v)}
                  disabled={busyId === v.id}
                  title="Delete view"
                  aria-label={`Delete view ${v.name}`}
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-zinc-500 opacity-0 transition-opacity hover:bg-zinc-800 hover:text-rose-300 group-hover:opacity-100 disabled:opacity-40"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-800 p-1">
            <button
              type="button"
              onClick={() => { setOpen(false); setSaveOpen(true); }}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm hover:bg-zinc-900"
            >
              <Plus className="h-3.5 w-3.5 text-indigo-400" />
              <span className="text-zinc-100">Save current view…</span>
            </button>
          </div>
        </div>
      )}

      <SaveViewDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        getCurrentConfig={getCurrentConfig}
        onSaved={(v) => {
          setSaveOpen(false);
          setViews((prev) => [v, ...prev]);
          setAppliedId(v.id);
          toast.success(`Saved "${v.name}"`);
        }}
      />
    </div>
  );
}

function SaveViewDialog({
  open, onClose, getCurrentConfig, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  getCurrentConfig: () => ContactsViewConfig;
  onSaved: (v: SavedView) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setName(""); }, [open]);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const res = await api.post<{ view: SavedView }>("/saved-views", {
        surface: SURFACE,
        name: trimmed,
        config: getCurrentConfig(),
      });
      onSaved(res.view);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save view");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Save current view" size="sm">
      <div>
        <label className="mb-1.5 block text-xs font-medium text-zinc-300">View name</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void submit(); }}
          placeholder="e.g. Hot leads, newest first"
          autoFocus
        />
        <p className="mt-2 text-xs text-zinc-500">
          Saves the current filter, sort, and search together so you can jump back to this exact view.
        </p>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!name.trim()}>Save view</Button>
      </ModalFooter>
    </Modal>
  );
}
